import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../i18n/LanguageContext';
import { useTranslatedContent } from '../hooks/useTranslatedContent';
import { InvestorDeckTrigger } from '../components/InvestorDeckModal';
import SEO from '../components/SEO';
import {
  ArrowRight, ChevronRight, Building2, HardHat, ShieldCheck, LineChart,
  Cpu, Layers, Camera, Ruler, Plane, Flame, Boxes, Sparkles, Globe2,
  Award, Handshake, Users, MapPin, Compass, ClipboardCheck, Wrench,
  Rocket, FileCheck2, TrendingUp, Building, Factory, GraduationCap,
  Map as MapIcon, ArrowUpRight, Download, Plus, Minus
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ---------------------------- CONTENT (i18n) ----------------------------
// Primary content is defined in FR and EN. ES/AR/ZH fall back to EN.
const CONTENT = {
  fr: {
    seo: {
      title: "PLEXTERRA — Développement immobilier | Canada & USA",
      description: "PLEXTERRA accompagne investisseurs, promoteurs et propriétaires dans l'analyse stratégique, la faisabilité, la conception technique et la réalisation de projets immobiliers résidentiels, multilogements et commerciaux au Canada et aux États-Unis.",
      keywords: "développement immobilier, analyse de terrain, étude de faisabilité, inspection bâtiment, inspection multilogement, loi 16, fonds de prévoyance, plans architecturaux, coordination architecturale, ingénierie, gestion de projet, promoteur immobilier, investisseur immobilier"
    },
    hero: {
      badge: "Firme de développement immobilier",
      title: "Transformer des terrains en projets rentables, réalisables et finançables.",
      subtitle: "PLEXTERRA accompagne les investisseurs, promoteurs et propriétaires dans l'analyse, l'optimisation, la conception technique et la réalisation de projets immobiliers résidentiels, multilogements et commerciaux.",
      ctaPrimary: "Demander une consultation",
      ctaSecondary: "Découvrir nos services",
      trust: ["Canada & États-Unis", "Professionnels certifiés", "Investisseurs institutionnels"]
    },
    divisions: {
      eyebrow: "Nos deux divisions",
      title: "Une firme, deux expertises intégrées",
      subtitle: "Du terrain brut à la livraison — un seul partenaire, deux divisions parfaitement synchronisées.",
      dev: {
        tag: "Division",
        title: "Développement immobilier",
        description: "Analyse stratégique, faisabilité et coordination de projets pour maximiser la valeur de chaque terrain.",
        items: [
          "Analyse stratégique de terrain",
          "Étude de faisabilité",
          "Optimisation réglementaire",
          "Optimisation financière",
          "Analyse de rentabilité",
          "Simulation SCHL / CMHC",
          "Analyse de densification",
          "Études de marché",
          "Optimisation du potentiel du terrain",
          "Coordination du développement",
          "Accompagnement investisseurs"
        ]
      },
      tech: {
        tag: "Division",
        title: "Services techniques",
        description: "Inspections, expertises, plans et gestion de projet livrés par des professionnels certifiés.",
        items: [
          "Inspection préachat",
          "Inspection préréception",
          "Inspection commerciale",
          "Inspection multilogement",
          "Loi 16",
          "Carnet d'entretien",
          "Fonds de prévoyance",
          "Expertises",
          "Vices cachés",
          "Plans architecturaux",
          "Coordination architecturale",
          "Coordination ingénierie",
          "Suivi de chantier",
          "Gestion de projet",
          "Conception résidentielle"
        ]
      }
    },
    tech: {
      eyebrow: "Technologies",
      title: "Des technologies de pointe au service de vos décisions",
      subtitle: "Chaque outil produit une donnée exploitable — pour décider, financer et construire avec certitude.",
      items: [
        { icon: "camera", name: "Caméra 360 Matterport", value: "Jumeaux numériques photoréalistes pour due diligence à distance et documentation d'état." },
        { icon: "plane", name: "Drone", value: "Orthophotos, volumétrie et suivi d'avancement de chantier haute précision." },
        { icon: "flame", name: "Thermographie", value: "Détection d'anomalies thermiques, ponts thermiques et défauts d'étanchéité." },
        { icon: "ruler", name: "Photogrammétrie", value: "Reconstitution 3D précise à partir d'images pour métrés, relevés et analyse patrimoniale." },
        { icon: "boxes", name: "BIM", value: "Modélisation coordonnée architecture-structure-mécanique pour éliminer les conflits." },
        { icon: "layers", name: "Autodesk Forma", value: "Analyse d'implantation, ensoleillement, vent et densité en phase pré-conception." },
        { icon: "cpu", name: "Jumeaux numériques", value: "Réplique numérique du bâtiment pour exploitation, entretien et fonds de prévoyance." },
        { icon: "map", name: "Analyse géospatiale", value: "SIG, cartographie du zonage et croisement de couches réglementaires et de marché." },
        { icon: "sparkles", name: "Intelligence artificielle", value: "Modèles prédictifs de rentabilité, détection de non-conformité, optimisation de programme." },
        { icon: "compass", name: "Visualisation 3D", value: "Rendus haute qualité pour ventes, financement, permis et communication municipale." }
      ]
    },
    why: {
      eyebrow: "Pourquoi PLEXTERRA",
      title: "La rigueur d'une firme institutionnelle. L'agilité d'un partenaire dédié.",
      items: [
        { icon: "layers", title: "Expertise multidisciplinaire", desc: "Architecture, ingénierie, finance, urbanisme et technologies numériques réunis sous un même toit." },
        { icon: "trending", title: "Optimisation financière", desc: "Modélisation IRR, DSCR, SCHL/APH Select et scénarios de refinancement institutionnel." },
        { icon: "cpu", title: "Technologies avancées", desc: "Matterport, drone, thermographie, BIM, IA — chaque décision appuyée par la donnée." },
        { icon: "handshake", title: "Coordination complète", desc: "Un seul point de contact pour architecture, ingénierie, chantier et permis." },
        { icon: "compass", title: "Vision stratégique", desc: "Nous transformons la contrainte réglementaire en avantage compétitif mesurable." },
        { icon: "users", title: "Accompagnement personnalisé", desc: "Chaque mandat est piloté par un expert senior de bout en bout." },
        { icon: "globe", title: "Canada et États-Unis", desc: "Mandats livrés dans plusieurs juridictions, en français, anglais et espagnol." },
        { icon: "award", title: "Professionnels certifiés", desc: "Membres d'ordres professionnels, inspecteurs accrédités, ingénieurs certifiés." }
      ]
    },
    process: {
      eyebrow: "Notre processus",
      title: "Neuf étapes maîtrisées, de l'idée à la livraison",
      steps: [
        { n: "01", title: "Découverte du projet", desc: "Cadrage de vos objectifs financiers, temporels et stratégiques." },
        { n: "02", title: "Analyse du terrain", desc: "Zonage, servitudes, contraintes physiques et potentiel réel." },
        { n: "03", title: "Étude de faisabilité", desc: "Programme optimal, coûts, revenus, rentabilité et financement." },
        { n: "04", title: "Optimisation", desc: "Densification, réglementation, structure financière, montage." },
        { n: "05", title: "Plans techniques", desc: "Conception architecturale, structurale et mécanique coordonnée." },
        { n: "06", title: "Coordination professionnelle", desc: "Ingénieurs, arpenteurs, notaires, entrepreneurs — orchestrés." },
        { n: "07", title: "Permis", desc: "Dépôt, négociation municipale, dérogations et suivi jusqu'à l'émission." },
        { n: "08", title: "Construction", desc: "Suivi de chantier, contrôle qualité, gestion des changements." },
        { n: "09", title: "Livraison", desc: "Réception, mise en service, remise du carnet d'entretien numérique." }
      ]
    },
    sectors: {
      eyebrow: "Nos secteurs",
      title: "Une expertise transversale sur tous les segments",
      items: [
        { icon: "building", name: "Résidentiel", desc: "Unifamilial, jumelé, cottage, projet haut de gamme." },
        { icon: "layers", name: "Multilogement", desc: "Duplex jusqu'à immeubles de 100+ unités, APH Select, SCHL." },
        { icon: "building2", name: "Commercial", desc: "Bureau, commerce de détail, mixte, franchises." },
        { icon: "factory", name: "Industriel léger", desc: "Entreposage, ateliers, distribution, flex space." },
        { icon: "graduation", name: "Institutionnel", desc: "Résidences pour aînés, garderies, cliniques, cliniques dentaires." },
        { icon: "map", name: "Développement foncier", desc: "Subdivision, lotissement, projet de développement complet." }
      ]
    },
    clients: {
      eyebrow: "Nos clients",
      title: "Un partenaire de confiance pour les décideurs de l'immobilier",
      items: [
        "Promoteurs immobiliers", "Investisseurs", "Family offices",
        "Entrepreneurs généraux", "Municipalités", "Institutions financières",
        "Propriétaires de terrains", "Courtiers immobiliers", "Courtiers hypothécaires",
        "Architectes", "Ingénieurs"
      ]
    },
    stats: {
      eyebrow: "Nos technologies en chiffres",
      title: "Une firme mesurable, transparente et redevable",
      subtitle: "Chiffres consolidés depuis notre CRM (HubSpot). Mise à jour continue."
    },
    finalCta: {
      title: "Prêt à transformer votre terrain en projet institutionnel ?",
      subtitle: "Réservez une consultation stratégique avec un expert senior — analyse initiale offerte pour les projets qualifiés.",
      primary: "Demander une consultation",
      secondary: "Nous contacter"
    },
    brochure: {
      eyebrow: "Notre approche",
      title: "Transformez votre site en opportunité",
      body: "Notre méthode institutionnelle en 7 étapes — de l'acquisition des données à la décision d'investissement. IA, BIM, VR, jumeau numérique.",
      cta: "Télécharger la brochure (FR)",
      ctaEn: "Download the brochure (EN)",
      thumbnail: "/brochures/brochure-fr.png",
      thumbnailEn: "/brochures/brochure-en.png",
      fileFr: "/brochures/brochure-fr.png",
      fileEn: "/brochures/brochure-en.png",
      link: "Voir tous les livrables"
    },
    faq: {
      eyebrow: "Questions fréquentes",
      title: "Tout ce qu'un promoteur doit savoir avant de nous contacter",
      subtitle: "Les six questions qui reviennent le plus souvent — des réponses directes, sans jargon.",
      items: [
        {
          question: "Quels services PLEXTERRA offre-t-il ?",
          answer: "PLEXTERRA offre des services de coordination et d'expertise technique en développement immobilier — études de faisabilité, optimisation SCHL / MLI Select, inspections de bâtiments, modélisation BIM, plans architecturaux et gestion de projet complète, au Canada et aux États-Unis."
        },
        {
          question: "PLEXTERRA travaille-t-il aux États-Unis ?",
          answer: "Oui. PLEXTERRA livre des mandats dans plusieurs provinces canadiennes et aux États-Unis, avec des services disponibles en français, en anglais et en espagnol."
        },
        {
          question: "Qu'est-ce que le programme MLI Select de la SCHL ?",
          answer: "MLI Select est un programme d'assurance prêt hypothécaire de la SCHL destiné aux projets locatifs multilogements. Pour les projets admissibles, il peut offrir des ratios prêt-valeur jusqu'à 95 % et des périodes d'amortissement jusqu'à 50 ans, en contrepartie d'engagements mesurables en matière d'abordabilité, d'accessibilité ou d'efficacité énergétique."
        },
        {
          question: "En quoi PLEXTERRA se distingue-t-il des autres firmes ?",
          answer: "PLEXTERRA coordonne architectes, ingénieurs, arpenteurs, notaires et entrepreneurs sous un même mandat — éliminant les zones grises entre les disciplines et livrant chaque projet de bout en bout."
        },
        {
          question: "Quels types de projets PLEXTERRA prend-il en charge ?",
          answer: "Projets résidentiels, multilogements (du duplex à plus de 100 unités), commerciaux, industriels légers, institutionnels et développement de terrains."
        },
        {
          question: "Comment démarrer un projet avec PLEXTERRA ?",
          answer: "Demandez une consultation gratuite sur plexterra.co/contact — notre équipe évaluera votre projet sous 48 heures."
        }
      ]
    }
  },
  en: {
    seo: {
      title: "PLEXTERRA — Real Estate Development | Canada & USA",
      description: "PLEXTERRA supports investors, developers and landowners in strategic analysis, feasibility, technical design and delivery of residential, multifamily and commercial real estate projects across Canada and the United States.",
      keywords: "real estate development, land analysis, feasibility study, property due diligence, building inspection, architectural design, project management"
    },
    hero: {
      badge: "Real Estate Development Firm",
      title: "Turning land into profitable, buildable and financeable projects.",
      subtitle: "PLEXTERRA supports investors, developers and landowners with analysis, optimization, technical design and delivery of residential, multifamily and commercial real estate projects.",
      ctaPrimary: "Request a consultation",
      ctaSecondary: "Explore our services",
      trust: ["Canada & USA", "Certified professionals", "Institutional-grade"]
    },
    divisions: {
      eyebrow: "Our two divisions",
      title: "One firm, two integrated capabilities",
      subtitle: "From raw land to delivery — one partner, two perfectly synchronized divisions.",
      dev: {
        tag: "Division",
        title: "Développement immobilier",
        description: "Strategic analysis, feasibility and project coordination to maximize the value of every lot.",
        items: [
          "Strategic land analysis", "Feasibility study", "Regulatory optimization",
          "Financial optimization", "Profitability analysis", "CMHC / SCHL simulation",
          "Densification analysis", "Market studies", "Land potential optimization",
          "Development coordination", "Investor advisory"
        ]
      },
      tech: {
        tag: "Division",
        title: "Technical Services",
        description: "Inspections, expertises, plans and project management delivered by certified professionals.",
        items: [
          "Pre-purchase inspection", "Pre-delivery inspection", "Commercial inspection",
          "Multifamily inspection", "Bill 16 (Loi 16)", "Maintenance logbook",
          "Reserve fund study", "Expertises", "Latent defects",
          "Architectural plans", "Architectural coordination", "Engineering coordination",
          "Site supervision", "Project management", "Residential design"
        ]
      }
    },
    tech: {
      eyebrow: "Technologies",
      title: "Advanced technologies powering better decisions",
      subtitle: "Every tool produces actionable data — to decide, finance and build with confidence.",
      items: [
        { icon: "camera", name: "Caméra 360 Matterport", value: "Photorealistic digital twins for remote due diligence and condition documentation." },
        { icon: "plane", name: "Drone", value: "Orthophotos, volumetrics and high-precision construction progress tracking." },
        { icon: "flame", name: "Thermography", value: "Detection of thermal anomalies, thermal bridges and envelope defects." },
        { icon: "ruler", name: "Photogrammetry", value: "Precise 3D reconstruction from imagery for measurements and heritage analysis." },
        { icon: "boxes", name: "BIM", value: "Coordinated architecture-structure-MEP modeling to eliminate conflicts." },
        { icon: "layers", name: "Autodesk Forma", value: "Massing, solar, wind and density analysis at the earliest design stage." },
        { icon: "cpu", name: "Digital twins", value: "Digital replicas for operations, maintenance and reserve fund planning." },
        { icon: "map", name: "Geospatial analysis", value: "GIS, zoning mapping and cross-referencing of regulatory and market layers." },
        { icon: "sparkles", name: "Artificial intelligence", value: "Predictive profitability models, compliance detection, program optimization." },
        { icon: "compass", name: "3D visualization", value: "High-quality renders for sales, financing, permits and municipal approvals." }
      ]
    },
    why: {
      eyebrow: "Why PLEXTERRA",
      title: "The rigor of an institutional firm. The agility of a dedicated partner.",
      items: [
        { icon: "layers", title: "Multidisciplinary expertise", desc: "Architecture, engineering, finance, urban planning and digital technology under one roof." },
        { icon: "trending", title: "Financial optimization", desc: "IRR, DSCR, CMHC/APH Select modeling and institutional refinancing scenarios." },
        { icon: "cpu", title: "Advanced technologies", desc: "Matterport, drone, thermography, BIM, AI — every decision backed by data." },
        { icon: "handshake", title: "Full coordination", desc: "One point of contact for architecture, engineering, construction and permits." },
        { icon: "compass", title: "Strategic vision", desc: "We turn regulatory constraints into measurable competitive advantages." },
        { icon: "users", title: "Personalized advisory", desc: "Every mandate is led end-to-end by a senior expert." },
        { icon: "globe", title: "Canada and USA", desc: "Mandates delivered across multiple jurisdictions, in French, English and Spanish." },
        { icon: "award", title: "Certified professionals", desc: "Members of professional orders, accredited inspectors, certified engineers." }
      ]
    },
    process: {
      eyebrow: "Our process",
      title: "Nine mastered steps, from idea to delivery",
      steps: [
        { n: "01", title: "Project discovery", desc: "Framing your financial, temporal and strategic objectives." },
        { n: "02", title: "Land analysis", desc: "Zoning, easements, physical constraints and real potential." },
        { n: "03", title: "Feasibility study", desc: "Optimal program, costs, revenue, profitability and financing." },
        { n: "04", title: "Optimization", desc: "Densification, regulations, financial structure, deal packaging." },
        { n: "05", title: "Technical plans", desc: "Architectural, structural and mechanical design, coordinated." },
        { n: "06", title: "Professional coordination", desc: "Engineers, surveyors, notaries, contractors — orchestrated." },
        { n: "07", title: "Permits", desc: "Filing, municipal negotiation, variances and follow-up through issuance." },
        { n: "08", title: "Construction", desc: "Site supervision, quality control, change management." },
        { n: "09", title: "Delivery", desc: "Handover, commissioning, digital maintenance logbook." }
      ]
    },
    sectors: {
      eyebrow: "Our sectors",
      title: "Cross-cutting expertise across every segment",
      items: [
        { icon: "building", name: "Residential", desc: "Single-family, semi-detached, cottage, high-end projects." },
        { icon: "layers", name: "Multifamily", desc: "Duplex to 100+ unit buildings, APH Select, CMHC financing." },
        { icon: "building2", name: "Commercial", desc: "Office, retail, mixed-use, franchises." },
        { icon: "factory", name: "Light industrial", desc: "Warehousing, workshops, distribution, flex space." },
        { icon: "graduation", name: "Institutional", desc: "Senior residences, daycare, clinics, dental practices." },
        { icon: "map", name: "Land development", desc: "Subdivision, lotting, full-scale land development." }
      ]
    },
    clients: {
      eyebrow: "Our clients",
      title: "A trusted partner for real estate decision-makers",
      items: [
        "Real estate developers", "Investors", "Family offices",
        "General contractors", "Municipalities", "Financial institutions",
        "Landowners", "Real estate brokers", "Mortgage brokers",
        "Architects", "Engineers"
      ]
    },
    stats: {
      eyebrow: "Our impact in numbers",
      title: "A measurable, transparent and accountable firm",
      subtitle: "Figures consolidated from our CRM (HubSpot). Updated continuously."
    },
    finalCta: {
      title: "Ready to turn your land into an institutional-grade project?",
      subtitle: "Book a strategic consultation with a senior expert — initial analysis offered for qualified projects.",
      primary: "Request a consultation",
      secondary: "Contact us"
    },
    brochure: {
      eyebrow: "Our approach",
      title: "Turn your site into an opportunity",
      body: "Our institutional 7-step method — from data acquisition to investment decision. AI, BIM, VR, digital twin.",
      cta: "Download the brochure (EN)",
      ctaEn: "Version française (FR)",
      thumbnail: "/brochures/brochure-en.png",
      thumbnailEn: "/brochures/brochure-fr.png",
      fileFr: "/brochures/brochure-fr.png",
      fileEn: "/brochures/brochure-en.png",
      link: "See all deliverables"
    },
    faq: {
      eyebrow: "Frequently asked",
      title: "Everything a developer should know before reaching out",
      subtitle: "The six questions we hear most often — direct answers, no jargon.",
      items: [
        {
          question: "What services does PLEXTERRA offer?",
          answer: "PLEXTERRA offers real estate development coordination and technical services including feasibility studies, CMHC/MLI Select optimization, building inspections, BIM modeling, architectural plans and full project management across Canada and the United States."
        },
        {
          question: "Does PLEXTERRA work in the United States?",
          answer: "Yes. PLEXTERRA delivers mandates across multiple Canadian provinces and in the United States, with services available in French, English and Spanish."
        },
        {
          question: "What is MLI Select CMHC?",
          answer: "MLI Select is a CMHC mortgage loan insurance program for multifamily rental projects that can offer loan-to-value ratios up to 95% and amortization periods up to 50 years for eligible projects meeting affordability, accessibility or energy efficiency criteria."
        },
        {
          question: "How does PLEXTERRA differ from other firms?",
          answer: "PLEXTERRA coordinates architects, engineers, surveyors, notaries and contractors under a single mandate — eliminating gaps between disciplines and delivering every project end-to-end."
        },
        {
          question: "What types of projects does PLEXTERRA handle?",
          answer: "Residential, multifamily (duplex to 100+ units), commercial, light industrial, institutional and land development projects."
        },
        {
          question: "How do I start a project with PLEXTERRA?",
          answer: "Request a free consultation at plexterra.co/contact — our team will assess your project within 48 hours."
        }
      ]
    }
  }
};

// ---------------------------- ICON HELPER ----------------------------
const ICONS = {
  camera: Camera, plane: Plane, flame: Flame, ruler: Ruler, boxes: Boxes,
  layers: Layers, cpu: Cpu, map: MapIcon, sparkles: Sparkles, compass: Compass,
  trending: TrendingUp, handshake: Handshake, users: Users, globe: Globe2,
  award: Award, building: Building, building2: Building2, factory: Factory,
  graduation: GraduationCap
};

const Icon = ({ name, className = "w-6 h-6" }) => {
  const Cmp = ICONS[name] || Sparkles;
  return <Cmp className={className} strokeWidth={1.5} />;
};

// ---------------------------- COMPONENT ----------------------------
// ---------------------------- FAQ SECTION ----------------------------
// Accordion-style FAQ with brand tokens (navy + gold). Each item is a
// stateful <button> for a11y (aria-expanded, aria-controls). The FAQPage
// JSON-LD is injected separately by <SEO faq={...} /> for rich results.
const FaqItem = ({ item, index, open, onToggle }) => {
  const panelId = `faq-panel-${index}`;
  const btnId = `faq-trigger-${index}`;
  return (
    <div
      className="border-b border-[#0B1E3F]/10 last:border-b-0"
      data-testid={`home-faq-item-${index}`}
    >
      <button
        id={btnId}
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={panelId}
        className="w-full flex items-center justify-between gap-4 py-6 text-left group"
        data-testid={`home-faq-trigger-${index}`}
      >
        <span className="font-serif text-lg sm:text-xl text-[#0B1E3F] leading-snug group-hover:text-[#C6A15A] transition-colors">
          {item.question}
        </span>
        <span
          className={`flex-none w-9 h-9 rounded-full border border-[#C6A15A] flex items-center justify-center transition-transform duration-300 ${open ? 'bg-[#C6A15A] text-white rotate-180' : 'text-[#C6A15A]'}`}
          aria-hidden="true"
        >
          {open ? <Minus className="w-4 h-4" strokeWidth={1.75} /> : <Plus className="w-4 h-4" strokeWidth={1.75} />}
        </span>
      </button>
      <div
        id={panelId}
        role="region"
        aria-labelledby={btnId}
        className={`grid transition-all duration-300 ease-out ${open ? 'grid-rows-[1fr] opacity-100 pb-6' : 'grid-rows-[0fr] opacity-0'}`}
      >
        <div className="overflow-hidden">
          <p className="text-[15px] sm:text-base text-gray-700 leading-relaxed pr-14">
            {item.answer}
          </p>
        </div>
      </div>
    </div>
  );
};

const FaqSection = ({ faq }) => {
  const [openIdx, setOpenIdx] = useState(0);
  if (!faq || !Array.isArray(faq.items) || faq.items.length === 0) return null;
  return (
    <section
      className="py-20 lg:py-28 bg-[#F5F1EA]"
      aria-labelledby="home-faq-heading"
      data-testid="home-faq-section"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12 lg:mb-16">
          <div className="text-xs tracking-[0.3em] uppercase text-[#C6A15A] mb-4">
            {faq.eyebrow}
          </div>
          <h2
            id="home-faq-heading"
            className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0B1E3F] leading-tight mb-4"
          >
            {faq.title}
          </h2>
          {faq.subtitle && (
            <p className="text-lg text-gray-600 leading-relaxed max-w-2xl">
              {faq.subtitle}
            </p>
          )}
        </div>
        <div className="bg-white rounded-sm shadow-[0_2px_20px_rgba(11,30,63,0.06)] px-6 sm:px-10">
          {faq.items.map((item, i) => (
            <FaqItem
              key={item.question}
              item={item}
              index={i}
              open={openIdx === i}
              onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default function HomePage() {
  const { language } = useLanguage();
  const { c } = useTranslatedContent(CONTENT, 'home');

  const [stats, setStats] = useState({ enabled: false, stats: [] });

  useEffect(() => {
    // Try to fetch public stats. Silently fail if endpoint unreachable.
    axios.get(`${BACKEND_URL}/api/stats/public`)
      .then(r => setStats(r.data || { enabled: false }))
      .catch(() => setStats({ enabled: false }));
  }, []);

  return (
    <div data-testid="home-page" className="bg-white">
      <SEO title={c.seo.title} description={c.seo.description} keywords={c.seo.keywords} url="/" faq={c.faq.items} />

      {/* ========== HERO ========== */}
      <section className="relative overflow-hidden bg-[#0B1E3F] text-white">
        {/* LCP-critical: responsive <img> lets the browser preload & pick the best size on mobile */}
        <img
          src="https://images.unsplash.com/photo-1759845565036-cbecbcfcb8e2?w=1600&q=70&auto=format&fit=crop"
          srcSet="
            https://images.unsplash.com/photo-1759845565036-cbecbcfcb8e2?w=640&q=70&auto=format&fit=crop 640w,
            https://images.unsplash.com/photo-1759845565036-cbecbcfcb8e2?w=1024&q=70&auto=format&fit=crop 1024w,
            https://images.unsplash.com/photo-1759845565036-cbecbcfcb8e2?w=1600&q=75&auto=format&fit=crop 1600w,
            https://images.unsplash.com/photo-1759845565036-cbecbcfcb8e2?w=2400&q=80&auto=format&fit=crop 2400w
          "
          sizes="100vw"
          alt=""
          aria-hidden="true"
          fetchpriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover opacity-25 pointer-events-none"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1E3F] via-[#0B1E3F]/95 to-[#0B1E3F]/60" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(198,161,90,0.15),transparent_60%)]" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-32 lg:pt-40 lg:pb-44">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 border border-[#C6A15A]/50 rounded-full mb-8" data-testid="hero-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-[#C6A15A]" />
              <span className="text-[#C6A15A] text-xs tracking-[0.2em] uppercase font-medium">{c.hero.badge}</span>
            </div>

            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.05] tracking-tight mb-8" data-testid="hero-title">
              {c.hero.title}
            </h1>

            <p className="text-lg lg:text-xl text-white/70 max-w-3xl leading-relaxed mb-12">
              {c.hero.subtitle}
            </p>

            <div className="flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="group inline-flex items-center gap-3 px-8 py-4 bg-[#C6A15A] text-[#0B1E3F] font-medium rounded-sm hover:bg-white transition-all"
                data-testid="hero-cta-primary"
              >
                {c.hero.ctaPrimary}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center gap-3 px-8 py-4 border border-white/25 text-white font-medium rounded-sm hover:border-white hover:bg-white/5 transition-all"
                data-testid="hero-cta-secondary"
              >
                {c.hero.ctaSecondary}
                <ChevronRight className="w-4 h-4" />
              </Link>
              <InvestorDeckTrigger
                variant="ghost"
                className="!border-white/25 !text-white hover:!border-white hover:!bg-white/5 rounded-sm"
                testId="hero-cta-deck"
              />
            </div>

            <div className="flex flex-wrap gap-x-8 gap-y-3 mt-16 pt-8 border-t border-white/10">
              {c.hero.trust.map((t, i) => (
                <div key={i} className="flex items-center gap-2 text-white/60 text-sm">
                  <span className="w-1 h-1 rounded-full bg-[#C6A15A]" />
                  {t}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========== DIVISIONS ========== */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-16">
            <div className="text-[#C6A15A] text-xs tracking-[0.25em] uppercase mb-4">{c.divisions.eyebrow}</div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0B1E3F] leading-tight mb-6">
              {c.divisions.title}
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">{c.divisions.subtitle}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            {/* Division 1 - Développement */}
            <div className="group relative overflow-hidden bg-[#0B1E3F] text-white p-8 lg:p-12 hover:shadow-2xl transition-all" data-testid="division-dev">
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#C6A15A]/5 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:bg-[#C6A15A]/10 transition-colors" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-[#C6A15A]/10 border border-[#C6A15A]/30 rounded-sm flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-[#C6A15A]" strokeWidth={1.5} />
                  </div>
                  <span className="text-[#C6A15A] text-xs tracking-[0.2em] uppercase">{c.divisions.dev.tag}</span>
                </div>
                <h3 className="font-serif text-2xl lg:text-3xl mb-4">{c.divisions.dev.title}</h3>
                <p className="text-white/70 leading-relaxed mb-8">{c.divisions.dev.description}</p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6 mb-10">
                  {c.divisions.dev.items.map((it, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-white/85">
                      <span className="text-[#C6A15A] mt-1">—</span>
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/services" className="inline-flex items-center gap-2 text-[#C6A15A] hover:text-white text-sm font-medium transition-colors" data-testid="division-dev-link">
                  {language === 'fr' ? 'En savoir plus' : 'Learn more'} <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* Division 2 - Services Techniques */}
            <div className="group relative overflow-hidden bg-[#F5F1E8] text-[#0B1E3F] p-8 lg:p-12 hover:shadow-2xl transition-all" data-testid="division-tech">
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#0B1E3F]/5 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:bg-[#0B1E3F]/10 transition-colors" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-[#0B1E3F]/10 border border-[#0B1E3F]/30 rounded-sm flex items-center justify-center">
                    <HardHat className="w-6 h-6 text-[#0B1E3F]" strokeWidth={1.5} />
                  </div>
                  <span className="text-[#0B1E3F]/70 text-xs tracking-[0.2em] uppercase">{c.divisions.tech.tag}</span>
                </div>
                <h3 className="font-serif text-2xl lg:text-3xl mb-4">{c.divisions.tech.title}</h3>
                <p className="text-[#0B1E3F]/75 leading-relaxed mb-8">{c.divisions.tech.description}</p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6 mb-10">
                  {c.divisions.tech.items.map((it, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#0B1E3F]/85">
                      <span className="text-[#C6A15A] mt-1">—</span>
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/services" className="inline-flex items-center gap-2 text-[#0B1E3F] hover:text-[#C6A15A] text-sm font-medium transition-colors" data-testid="division-tech-link">
                  {language === 'fr' ? 'En savoir plus' : 'Learn more'} <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== TECHNOLOGIES ========== */}
      <section className="py-24 lg:py-32 bg-[#0B1E3F] text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(198,161,90,0.08),transparent_50%)]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-16">
            <div className="text-[#C6A15A] text-xs tracking-[0.25em] uppercase mb-4">{c.tech.eyebrow}</div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight mb-6">{c.tech.title}</h2>
            <p className="text-lg text-white/70 leading-relaxed">{c.tech.subtitle}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-px bg-white/10" data-testid="tech-grid">
            {c.tech.items.map((it, i) => (
              <div key={i} className="bg-[#0B1E3F] p-8 hover:bg-[#0B1E3F]/50 transition-colors group" data-testid={`tech-item-${i}`}>
                <div className="w-12 h-12 border border-[#C6A15A]/30 rounded-sm flex items-center justify-center mb-6 group-hover:bg-[#C6A15A]/10 transition-colors">
                  <Icon name={it.icon} className="w-5 h-5 text-[#C6A15A]" />
                </div>
                <h3 className="font-serif text-lg mb-3">{it.name}</h3>
                <p className="text-sm text-white/60 leading-relaxed">{it.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== WHY ========== */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-16">
            <div className="text-[#C6A15A] text-xs tracking-[0.25em] uppercase mb-4">{c.why.eyebrow}</div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0B1E3F] leading-tight">{c.why.title}</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-testid="why-grid">
            {c.why.items.map((it, i) => (
              <div key={i} className="group border-t border-gray-200 pt-6 hover:border-[#C6A15A] transition-colors" data-testid={`why-item-${i}`}>
                <div className="w-10 h-10 mb-4 text-[#C6A15A]">
                  <Icon name={it.icon} className="w-10 h-10" />
                </div>
                <h3 className="font-serif text-lg text-[#0B1E3F] mb-3">{it.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{it.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== PROCESS ========== */}
      <section className="py-24 lg:py-32 bg-[#F5F1E8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-16">
            <div className="text-[#C6A15A] text-xs tracking-[0.25em] uppercase mb-4">{c.process.eyebrow}</div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0B1E3F] leading-tight">{c.process.title}</h2>
          </div>

          <div className="relative">
            <div className="hidden lg:block absolute top-8 left-0 right-0 h-px bg-[#0B1E3F]/10" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-9 gap-8" data-testid="process-steps">
              {c.process.steps.map((s, i) => (
                <div key={i} className="relative group" data-testid={`process-step-${i}`}>
                  <div className="relative">
                    <div className="w-16 h-16 bg-white border-2 border-[#0B1E3F] rounded-full flex items-center justify-center mb-4 group-hover:border-[#C6A15A] group-hover:bg-[#C6A15A] transition-all">
                      <span className="font-serif text-[#0B1E3F] text-lg group-hover:text-white transition-colors">{s.n}</span>
                    </div>
                  </div>
                  <h3 className="font-serif text-base text-[#0B1E3F] mb-2 leading-tight">{s.title}</h3>
                  <p className="text-xs text-gray-600 leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========== SECTORS ========== */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-16">
            <div className="text-[#C6A15A] text-xs tracking-[0.25em] uppercase mb-4">{c.sectors.eyebrow}</div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0B1E3F] leading-tight">{c.sectors.title}</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="sectors-grid">
            {c.sectors.items.map((s, i) => (
              <div key={i} className="group border border-gray-200 p-8 hover:border-[#C6A15A] hover:shadow-lg transition-all" data-testid={`sector-item-${i}`}>
                <div className="w-12 h-12 mb-6 text-[#0B1E3F] group-hover:text-[#C6A15A] transition-colors">
                  <Icon name={s.icon} className="w-10 h-10" />
                </div>
                <h3 className="font-serif text-xl text-[#0B1E3F] mb-3">{s.name}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== CLIENTS ========== */}
      <section className="py-24 lg:py-32 bg-[#0B1E3F] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-16">
            <div className="text-[#C6A15A] text-xs tracking-[0.25em] uppercase mb-4">{c.clients.eyebrow}</div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight">{c.clients.title}</h2>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-white/10" data-testid="clients-grid">
            {c.clients.items.map((cl, i) => (
              <div key={i} className="bg-[#0B1E3F] px-6 py-8 flex items-center gap-3 hover:bg-white/5 transition-colors" data-testid={`client-item-${i}`}>
                <span className="w-1.5 h-1.5 bg-[#C6A15A] flex-shrink-0" />
                <span className="text-sm">{cl}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== STATS (hidden until HubSpot enables) ========== */}
      {stats?.enabled && Array.isArray(stats.stats) && (
        <section className="py-24 lg:py-32 bg-[#F5F1E8]" data-testid="stats-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mb-16">
              <div className="text-[#C6A15A] text-xs tracking-[0.25em] uppercase mb-4">{c.stats.eyebrow}</div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0B1E3F] leading-tight mb-4">{c.stats.title}</h2>
              <p className="text-sm text-gray-600">{c.stats.subtitle}</p>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-8">
              {stats.stats.filter(s => s.value !== null && s.value !== undefined).map((s, i) => {
                const raw = Number(s.value);
                const isDollar = s.unit === '$';
                let display;
                if (Number.isFinite(raw)) {
                  if (Math.abs(raw) >= 1_000_000) display = `${(raw / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
                  else if (Math.abs(raw) >= 1_000) display = `${(raw / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
                  else display = raw.toLocaleString(language === 'fr' ? 'fr-CA' : 'en-CA');
                } else {
                  display = String(s.value);
                }
                return (
                  <div key={i} className="border-t border-[#0B1E3F]/20 pt-6" data-testid={`stat-item-${i}`}>
                    <div className="font-serif text-4xl lg:text-5xl text-[#0B1E3F]">
                      {isDollar ? `${display}$` : `${display}${s.unit || ''}`}
                    </div>
                    <div className="mt-2 text-sm text-gray-600 uppercase tracking-wider">
                      {language === 'fr' ? s.label_fr : s.label_en}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ========== BROCHURE ========== */}
      <section className="py-24 lg:py-32 bg-[#F7F5EF]" data-testid="home-brochure">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-5 order-2 lg:order-1">
              <Link to="/projets" className="group block relative" data-testid="brochure-thumbnail-link">
                <div className="absolute -top-3 -left-3 w-16 h-16 border-t-2 border-l-2 border-[#C6A15A]" aria-hidden="true" />
                <div className="absolute -bottom-3 -right-3 w-16 h-16 border-b-2 border-r-2 border-[#C6A15A]" aria-hidden="true" />
                <div className="relative overflow-hidden shadow-2xl">
                  <img
                    src={c.brochure.thumbnail}
                    alt={c.brochure.title}
                    className="w-full h-auto transition-transform duration-500 group-hover:scale-[1.02]"
                    loading="lazy"
                  />
                </div>
              </Link>
            </div>

            <div className="lg:col-span-7 order-1 lg:order-2">
              <div className="text-[#C6A15A] text-xs tracking-[0.3em] uppercase mb-4">
                {c.brochure.eyebrow}
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0B1E3F] leading-[1.1] mb-6">
                {c.brochure.title}
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-10 max-w-xl">
                {c.brochure.body}
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href={c.brochure.fileEn || '/brochures/brochure-en.png'}
                  download="PLEXTERRA-Brochure-EN.png"
                  className="inline-flex items-center gap-3 px-6 py-3.5 bg-[#0B1E3F] text-white text-xs tracking-[0.2em] uppercase hover:bg-[#C6A15A] hover:text-[#0B1E3F] transition-colors"
                  data-testid="home-brochure-download-en"
                >
                  <Download className="w-4 h-4" strokeWidth={2} />
                  {c.brochure.cta}
                </a>
                <a
                  href={c.brochure.fileFr || '/brochures/brochure-fr.png'}
                  download="PLEXTERRA-Brochure-FR.png"
                  className="inline-flex items-center gap-3 px-6 py-3.5 border border-[#0B1E3F] text-[#0B1E3F] text-xs tracking-[0.2em] uppercase hover:bg-[#0B1E3F] hover:text-white transition-colors"
                  data-testid="home-brochure-download-fr"
                >
                  <Download className="w-4 h-4" strokeWidth={2} />
                  {c.brochure.ctaEn}
                </a>
                <Link
                  to="/projets"
                  className="inline-flex items-center gap-2 px-6 py-3.5 text-xs tracking-[0.2em] uppercase text-[#0B1E3F]/70 hover:text-[#C6A15A] transition-colors"
                  data-testid="home-brochure-see-deliverables"
                >
                  {c.brochure.link}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== FAQ ========== */}
      <FaqSection faq={c.faq} />

      {/* ========== FINAL CTA ========== */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0B1E3F] leading-tight mb-6">
            {c.finalCta.title}
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-10 max-w-2xl mx-auto">
            {c.finalCta.subtitle}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="group inline-flex items-center gap-3 px-8 py-4 bg-[#0B1E3F] text-white font-medium rounded-sm hover:bg-[#C6A15A] hover:text-[#0B1E3F] transition-all" data-testid="final-cta-primary">
              {c.finalCta.primary}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-3 px-8 py-4 border border-[#0B1E3F] text-[#0B1E3F] font-medium rounded-sm hover:bg-[#0B1E3F] hover:text-white transition-all" data-testid="final-cta-secondary">
              {c.finalCta.secondary}
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
